import { useCallback, useMemo } from 'react';

import { useShowConsumerBacker, useUpdateBacker } from '@givebacks/api/backer';
import { getErrorMessage_v2 } from 'core/Utils';
import { useNavigate } from 'react-router-dom';
import { notifications } from 'ui';

import usePlatformService from '../../../hooks/usePlatformService';
import Native from '../../../utils/native';

const ROUTES = {
  ONBOARDING_PERSONALIZED: '/home/onboarding/your-personalized-start',
  ONBOARDING_INTENT: '/onboarding/intent',
};

const NATIVE_ROUTES = {
  ONBOARDING_PERSONALIZED: '/onboarding/your-personalized-start',
  ONBOARDING_INTENT: '/native-onboarding/intent',
};

const PERSONALIZED_TEXT = {
  COMPLETED: 'Start with a few simple steps picked just for you',
  INCOMPLETE: "Tell us what you're interested in and we'll customize your experience.",
};

export const useOnboardingState = () => {
  const navigate = useNavigate();
  const { data: backer, isLoading: isLoadingBacker } = useShowConsumerBacker();
  const { mutate: updateBacker, isPending: isUpdatingBacker } = useUpdateBacker();

  const Platform = usePlatformService();

  const shouldShow = useMemo(() => {
    return !backer?.backer?.personalized_modal_dismissed;
  }, [backer?.backer?.personalized_modal_dismissed]);

  const personalizedText = useMemo(() => {
    return backer?.backer?.onboarding_complete
      ? PERSONALIZED_TEXT.COMPLETED
      : PERSONALIZED_TEXT.INCOMPLETE;
  }, [backer?.backer?.onboarding_complete]);

  const onboardingCompleteActions = useMemo(() => {
    return Platform.select({
      default: () => {
        navigate(ROUTES.ONBOARDING_PERSONALIZED);
      },
      native: () => {
        // We navigate to the onboarding page for native instead of the modal on the home page
        navigate(NATIVE_ROUTES.ONBOARDING_PERSONALIZED);
      },
    });
  }, [Platform, navigate]);

  const defaultActions = useMemo(() => {
    return Platform.select({
      default: () => {
        navigate(ROUTES.ONBOARDING_INTENT);
      },
      native: () => {
        Native.postAction('navigate', { url: NATIVE_ROUTES.ONBOARDING_INTENT });
      },
    });
  }, [Platform, navigate]);

  const handleGetStarted = useCallback(() => {
    if (backer?.backer?.onboarding_complete) {
      onboardingCompleteActions();
    } else {
      defaultActions();
    }
  }, [backer?.backer?.onboarding_complete, onboardingCompleteActions, defaultActions]);

  const handleDismiss = useCallback(() => {
    updateBacker(
      { backer: { personalized_modal_dismissed: true } },
      {
        onError: (error) => {
          notifications.show({
            message: getErrorMessage_v2(error, 'Error dismissing personalized start'),
            variant: 'error',
          });
        },
      }
    );
  }, [updateBacker]);

  return {
    shouldShow,
    personalizedText,
    handleGetStarted,
    handleDismiss,
    isLoadingBacker: isLoadingBacker || isUpdatingBacker,
  };
};
